// src/pages/AdminDashboard.jsx
import React, { useEffect, useState } from "react";
import {
  collection, query, orderBy, onSnapshot,
  doc, updateDoc, deleteDoc, serverTimestamp
} from "firebase/firestore";
import { db } from "../lib/firebase";
import { useAuth } from "../lib/AuthContext";
import { formatDistanceToNow } from "date-fns";
import { isFilterCleared, matchesOptionalFilter } from "../lib/filters";

const INDUSTRIES = [
  "AI & Automation", "Sustainability", "Supply Chain", "FinTech",
  "Healthcare", "Logistics", "Retail Tech", "Cybersecurity", "Other"
];
const FILTER_INDUSTRIES = ["All", ...INDUSTRIES.filter(i => i !== "Other"), "Other"];
const FILTER_BUDGETS = ["Any", "<$50k", "$50k–$150k", "$150k–$500k", "$500k+"];
const FILTER_TIMELINES = ["Any", "<1 month", "1–3 months", "3–6 months", "6+ months"];
const STAGES = ["Any stage", "Pre-seed", "Seed", "Series A", "Series B+"];
const NEED_STATUSES = [
  { value: "pending_review", label: "Pending review" },
  { value: "new", label: "Approved (new)" },
  { value: "open", label: "Live (open)" },
  { value: "closing", label: "Closing soon" },
  { value: "closed", label: "Closed" },
  { value: "changes_requested", label: "Changes requested" },
  { value: "rejected", label: "Rejected" },
];

export default function AdminDashboard() {
  const { userProfile } = useAuth();
  const [tab, setTab] = useState("needs");
  const [needs, setNeeds] = useState([]);
  const [pitches, setPitches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState({});
  const [expanded, setExpanded] = useState(null);
  const [editingNeed, setEditingNeed] = useState(null);
  const [toast, setToast] = useState(null);
  const [industry, setIndustry] = useState("All");
  const [budget, setBudget] = useState("Any");
  const [timeline, setTimeline] = useState("Any");
  const [sortBy, setSortBy] = useState("newest");
  const [stageFilter, setStageFilter] = useState("all"); // all | pending | approved

  useEffect(() => {
    const qNeeds = query(collection(db, "needs"), orderBy("createdAt", "desc"));
    const unsubNeeds = onSnapshot(qNeeds, snap => {
      setNeeds(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    const qPitches = query(collection(db, "pitches"), orderBy("createdAt", "desc"));
    const unsubPitches = onSnapshot(qPitches, snap => {
      setPitches(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => { unsubNeeds(); unsubPitches(); };
  }, []);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  function writeErrorMessage(err) {
    if (err?.code === "permission-denied") {
      return "You don’t have permission to change this challenge. Try signing out and back in, then retry.";
    }
    return err?.message || "Failed to save changes. Please try again.";
  }

  async function updateNeedStatus(id, status) {
    try {
      await updateDoc(doc(db, "needs", id), {
        status,
        reviewedAt: serverTimestamp(),
        reviewedBy: userProfile?.displayName || "Admin",
        adminFeedback: feedback[id] || ""
      });
      setFeedback(f => ({ ...f, [id]: "" }));
      setToast(status === "rejected" ? "Challenge rejected" : "Challenge updated");
    } catch (err) {
      console.error("updateNeedStatus", err);
      setToast(writeErrorMessage(err));
    }
  }

  async function updatePitchStatus(id, status) {
    await updateDoc(doc(db, "pitches", id), {
      status,
      reviewedAt: serverTimestamp(),
      reviewedBy: userProfile?.displayName || "Admin",
    });
  }

  async function sendPitchFeedback(id, feedbackText) {
    await updateDoc(doc(db, "pitches", id), {
      adminFeedback: feedbackText.trim(),
      feedbackUpdatedAt: new Date(),
    });
    setToast("Feedback sent to startup");
  }

  async function saveChallengeEdits(id, data) {
    await updateDoc(doc(db, "needs", id), {
      title: data.title.trim(),
      description: data.description.trim(),
      industry: data.industry || "",
      startupStage: data.startupStage || "Any stage",
      status: data.status,
      additionalContext: (data.additionalContext || "").trim(),
      updatedAt: serverTimestamp(),
      editedBy: userProfile?.displayName || "Admin",
      editedAt: serverTimestamp(),
    });
    setEditingNeed(null);
    setToast("Challenge updated successfully");
  }

  async function deleteNeed(id, title) {
    const label = title ? `"${title}"` : "this challenge";
    if (!window.confirm(`Delete ${label} permanently? This cannot be undone.`)) return;
    try {
      await deleteDoc(doc(db, "needs", id));
      if (expanded === id) setExpanded(null);
      if (editingNeed?.id === id) setEditingNeed(null);
      setToast("Challenge deleted");
    } catch (err) {
      console.error("deleteNeed", err);
      setToast(writeErrorMessage(err));
    }
  }

  const isNeedPending = (n) => n.status === "pending_review";
  const isNeedApproved = (n) => ["new", "open", "closing"].includes(n.status);
  const isPitchPending = (p) => p.status === "pending_review" || p.status === "pending";
  const isPitchApproved = (p) => p.status === "approved";

  const pendingNeeds = needs.filter(isNeedPending);
  const pendingPitches = pitches.filter(isPitchPending);
  const approvedNeeds = needs.filter(isNeedApproved);
  const approvedPitches = pitches.filter(isPitchApproved);

  const filtersActive =
    !isFilterCleared(industry) || !isFilterCleared(budget) || !isFilterCleared(timeline);
  const filteredNeeds = needs
    .filter(n => {
      if (stageFilter === "pending" && !isNeedPending(n)) return false;
      if (stageFilter === "approved" && !isNeedApproved(n)) return false;
      if (!matchesOptionalFilter(n.industry, industry)) return false;
      if (!matchesOptionalFilter(n.budget, budget)) return false;
      if (!matchesOptionalFilter(n.timeline, timeline)) return false;
      return true;
    })
    .sort((a, b) => {
      const aTime = a.createdAt?.toMillis?.() || 0;
      const bTime = b.createdAt?.toMillis?.() || 0;
      return sortBy === "oldest" ? aTime - bTime : bTime - aTime;
    });

  const filteredPitches = pitches
    .filter(p => {
      if (stageFilter === "pending" && !isPitchPending(p)) return false;
      if (stageFilter === "approved" && !isPitchApproved(p)) return false;
      return true;
    })
    .sort((a, b) => {
      const aTime = a.createdAt?.toMillis?.() || 0;
      const bTime = b.createdAt?.toMillis?.() || 0;
      return sortBy === "oldest" ? aTime - bTime : bTime - aTime;
    });

  function clearFilters() {
    setIndustry("All");
    setBudget("Any");
    setTimeline("Any");
  }

  function selectStage(nextTab, nextStage) {
    if (tab === nextTab && stageFilter === nextStage) {
      setStageFilter("all");
      return;
    }
    setTab(nextTab);
    setStageFilter(nextStage);
    setExpanded(null);
  }

  function switchTab(nextTab) {
    setTab(nextTab);
    setStageFilter("all");
    setExpanded(null);
  }

  const stats = [
    {
      id: "needs-pending",
      label: "Challenges pending",
      value: pendingNeeds.length,
      tab: "needs",
      stage: "pending",
    },
    {
      id: "pitches-pending",
      label: "Solutions pending",
      value: pendingPitches.length,
      tab: "pitches",
      stage: "pending",
    },
    {
      id: "needs-approved",
      label: "Challenges approved",
      value: approvedNeeds.length,
      tab: "needs",
      stage: "approved",
    },
    {
      id: "pitches-approved",
      label: "Solutions approved",
      value: approvedPitches.length,
      tab: "pitches",
      stage: "approved",
    },
  ];

  return (
    <div className="admin-dashboard app-shell">
      {toast && (
        <div className="admin-toast" role="status" aria-live="polite">
          <span className="admin-toast-dot" aria-hidden="true" />
          {toast}
        </div>
      )}

      <div style={{ marginBottom: 28 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 8,
            background: "linear-gradient(135deg, #2563d4, #1a3a8f)",
            display: "flex", alignItems: "center", justifyContent: "center"
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
          </div>
          <h1 style={{ fontSize: 26, fontWeight: 800, color: "var(--text)", fontFamily: "Barlow, sans-serif", textTransform: "uppercase" }}>
            Mana Tech Access Admin
          </h1>
        </div>
        <p style={{ fontSize: 15, color: "var(--text-3)" }}>Review and approve challenges and solutions before they go live</p>
      </div>

      <div className="admin-stat-grid" role="group" aria-label="Segment by stage">
        {stats.map(s => {
          const active = tab === s.tab && stageFilter === s.stage;
          return (
            <button
              key={s.id}
              type="button"
              className={`admin-stat-card${active ? " is-active" : ""}`}
              aria-pressed={active}
              onClick={() => selectStage(s.tab, s.stage)}
            >
              <div className="admin-stat-value">{s.value}</div>
              <div className="admin-stat-label">{s.label}</div>
            </button>
          );
        })}
      </div>

      <div style={{ display: "flex", gap: 4, marginBottom: 20, flexWrap: "wrap", alignItems: "center" }}>
        {["needs", "pitches"].map(t => (
          <button key={t} type="button" onClick={() => switchTab(t)} style={{
            padding: "8px 20px", borderRadius: 99, fontSize: 15, fontWeight: tab === t ? 600 : 400,
            background: tab === t ? "rgba(37,99,212,0.2)" : "transparent",
            color: tab === t ? "#60a5fa" : "var(--text-2)",
            border: tab === t ? "1px solid rgba(37,99,212,0.3)" : "1px solid transparent",
            cursor: "pointer"
          }}>
            {t === "needs" ? "Challenges" : "Solutions"}
            {t === "needs" && pendingNeeds.length > 0 && (
              <span style={{ marginLeft: 6, background: "#75faaa", color: "#000", borderRadius: 99, padding: "1px 7px", fontSize: 13, fontWeight: 700 }}>{pendingNeeds.length}</span>
            )}
            {t === "pitches" && pendingPitches.length > 0 && (
              <span style={{ marginLeft: 6, background: "#75faaa", color: "#000", borderRadius: 99, padding: "1px 7px", fontSize: 13, fontWeight: 700 }}>{pendingPitches.length}</span>
            )}
          </button>
        ))}
        {stageFilter !== "all" && (
          <button
            type="button"
            className="admin-filter-clear"
            style={{ marginLeft: "auto", alignSelf: "center", minHeight: 36, padding: "6px 8px" }}
            onClick={() => setStageFilter("all")}
          >
            Show all {tab === "needs" ? "challenges" : "solutions"}
          </button>
        )}
      </div>

      {loading ? (
        <div className="page-loading"><div className="spinner" style={{ width: 28, height: 28 }}></div></div>
      ) : tab === "needs" ? (
        <div className="admin-split">
          <aside aria-label="Filter challenges">
          <div className="admin-filter-bar" role="search" aria-label="Filter challenges">
            <label className="admin-filter-field">
              <span>Submitted</span>
              <select className="select admin-filter-select" value={sortBy} onChange={e => setSortBy(e.target.value)}>
                <option value="newest">Latest first</option>
                <option value="oldest">Oldest first</option>
              </select>
            </label>
            <label className="admin-filter-field">
              <span>Industry</span>
              <select className="select admin-filter-select" value={industry} onChange={e => setIndustry(e.target.value)}>
                {FILTER_INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
              </select>
            </label>
            <label className="admin-filter-field">
              <span>Budget</span>
              <select className="select admin-filter-select" value={budget} onChange={e => setBudget(e.target.value)}>
                {FILTER_BUDGETS.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </label>
            <label className="admin-filter-field">
              <span>Timeline</span>
              <select className="select admin-filter-select" value={timeline} onChange={e => setTimeline(e.target.value)}>
                {FILTER_TIMELINES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>
            {filtersActive && (
              <button type="button" className="admin-filter-clear" onClick={clearFilters}>
                Clear filters
              </button>
            )}
          </div>
          </aside>

          <div className="app-main">
          <div style={{ fontSize: 15, color: "var(--text-3)", marginBottom: 12 }}>
            {filteredNeeds.length} challenge{filteredNeeds.length !== 1 ? "s" : ""}
            {stageFilter !== "all" ? ` · ${stageFilter}` : ""}
            {filtersActive ? " matching filters" : ""}
          </div>

          <div className="app-card-list">
            {needs.length === 0 ? (
              <div className="empty-state card" style={{ padding: 48 }}><h3>No challenges yet</h3></div>
            ) : filteredNeeds.length === 0 ? (
              <div className="empty-state card" style={{ padding: 48 }}>
                <h3>No challenges match</h3>
                <p>Try another stage segment or adjust industry, budget, or timeline.</p>
                <button
                  type="button"
                  className="btn btn-outline"
                  style={{ marginTop: 12 }}
                  onClick={() => { clearFilters(); setStageFilter("all"); }}
                >
                  Reset filters
                </button>
              </div>
            ) : filteredNeeds.map(need => (
              <NeedCard
                key={need.id}
                need={need}
                expanded={expanded === need.id}
                onToggle={() => setExpanded(expanded === need.id ? null : need.id)}
                onEdit={() => setEditingNeed(need)}
                onDelete={() => deleteNeed(need.id, need.title)}
                feedback={feedback[need.id] || ""}
                onFeedback={v => setFeedback(f => ({ ...f, [need.id]: v }))}
                onApprove={() => updateNeedStatus(need.id, "new")}
                onReject={() => updateNeedStatus(need.id, "rejected")}
                onRequestChanges={() => updateNeedStatus(need.id, "changes_requested")}
              />
            ))}
          </div>
          </div>
        </div>
      ) : (
        <div>
          <div style={{ fontSize: 15, color: "var(--text-3)", marginBottom: 12 }}>
            {filteredPitches.length} solution{filteredPitches.length !== 1 ? "s" : ""}
            {stageFilter !== "all" ? ` · ${stageFilter}` : ""}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {pitches.length === 0 ? (
              <div className="empty-state card" style={{ padding: 48 }}><h3>No solutions yet</h3></div>
            ) : filteredPitches.length === 0 ? (
              <div className="empty-state card" style={{ padding: 48 }}>
                <h3>No solutions match</h3>
                <p>Try another stage segment above.</p>
                <button type="button" className="btn btn-outline" style={{ marginTop: 12 }} onClick={() => setStageFilter("all")}>
                  Show all solutions
                </button>
              </div>
            ) : filteredPitches.map(pitch => (
              <PitchCard
                key={pitch.id}
                pitch={pitch}
                expanded={expanded === pitch.id}
                onToggle={() => setExpanded(expanded === pitch.id ? null : pitch.id)}
                onApprove={() => updatePitchStatus(pitch.id, "approved")}
                onReject={() => updatePitchStatus(pitch.id, "declined")}
                onRequestChanges={() => updatePitchStatus(pitch.id, "changes_requested")}
                onSendFeedback={sendPitchFeedback}
              />
            ))}
          </div>
        </div>
      )}

      {editingNeed && (
        <EditChallengeModal
          need={editingNeed}
          onClose={() => setEditingNeed(null)}
          onSave={saveChallengeEdits}
        />
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending_review: { label: "Pending review", bg: "rgba(117,250,170,0.15)", color: "#75faaa" },
    pending: { label: "Pending review", bg: "rgba(117,250,170,0.15)", color: "#75faaa" },
    new: { label: "Approved", bg: "var(--teal-light)", color: "var(--teal)" },
    open: { label: "Live", bg: "var(--teal-light)", color: "var(--teal)" },
    rejected: { label: "Rejected", bg: "var(--coral-light)", color: "var(--coral-text)" },
    changes_requested: { label: "Changes requested", bg: "var(--purple-light)", color: "var(--purple-text)" },
    approved: { label: "Approved", bg: "var(--teal-light)", color: "var(--teal)" },
    declined: { label: "Declined", bg: "var(--coral-light)", color: "var(--coral-text)" },
    shortlisted: { label: "Shortlisted", bg: "var(--purple-light)", color: "var(--purple-text)" },
    matched: { label: "Matched", bg: "var(--teal-light)", color: "var(--teal)" },
    closing: { label: "Closing soon", bg: "rgba(117,250,170,0.15)", color: "#75faaa" },
    closed: { label: "Closed", bg: "rgba(255,255,255,0.05)", color: "var(--text-3)" },
  };
  const s = map[status] || { label: status, bg: "rgba(255,255,255,0.05)", color: "var(--text-3)" };
  return (
    <span style={{ fontSize: 13, fontWeight: 600, padding: "3px 10px", borderRadius: 99, background: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
}

function ActionButtons({ onApprove, onReject, onRequestChanges, feedback, onFeedback, type }) {
  return (
    <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 10 }}>
      <div className="form-group">
        <label className="form-label">Feedback to {type === "need" ? "corporation" : "startup"} (optional)</label>
        <textarea className="textarea" style={{ minHeight: 70 }} value={feedback}
          onChange={e => onFeedback(e.target.value)}
          placeholder="Add a note explaining your decision or what needs to change..." />
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button type="button" onClick={onApprove} style={{
          flex: 1, minWidth: 120, padding: "9px", borderRadius: "var(--radius)", fontSize: 15, fontWeight: 600, cursor: "pointer",
          background: "var(--teal-light)", color: "var(--teal)", border: "1px solid rgba(117,250,170,0.25)"
        }}>✓ Approve</button>
        <button type="button" onClick={onRequestChanges} style={{
          flex: 1, minWidth: 120, padding: "9px", borderRadius: "var(--radius)", fontSize: 15, fontWeight: 600, cursor: "pointer",
          background: "var(--purple-light)", color: "var(--purple-text)", border: "1px solid rgba(83,74,183,0.25)"
        }}>↩ Request changes</button>
        <button type="button" onClick={onReject} style={{
          flex: 1, minWidth: 120, padding: "9px", borderRadius: "var(--radius)", fontSize: 15, fontWeight: 600, cursor: "pointer",
          background: "var(--coral-light)", color: "var(--coral-text)", border: "1px solid rgba(220,80,60,0.2)"
        }}>✗ Reject</button>
      </div>
    </div>
  );
}

function DetailBlock({ label, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{
        fontSize: 13, fontWeight: 600, color: "var(--text-3)",
        textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6
      }}>{label}</div>
      <div style={{ fontSize: 15, color: "var(--text-2)", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>
        {children}
      </div>
    </div>
  );
}

function NeedCard({ need, expanded, onToggle, onEdit, onDelete, feedback, onFeedback, onApprove, onReject, onRequestChanges }) {
  const timeAgo = need.createdAt?.toDate ? formatDistanceToNow(need.createdAt.toDate(), { addSuffix: true }) : "";
  const hasRequirements = Boolean(need.additionalContext?.trim());

  return (
    <div className="card" style={{ padding: "16px 18px", borderLeft: need.status === "pending_review" ? "3px solid #75faaa" : "1px solid var(--border)" }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
            <StatusBadge status={need.status} />
            {need.industry && <span className="admin-meta-chip">{need.industry}</span>}
            <span style={{ fontSize: 14, color: "var(--text-3)" }}>{timeAgo}</span>
          </div>
          <div style={{ fontSize: 16, fontWeight: 600, color: "var(--text)", marginBottom: 4 }}>{need.title}</div>
          <div style={{ fontSize: 14, color: "var(--text-3)", marginBottom: 8 }}>
            {need.corpName}
            {need.budget ? ` · ${need.budget}` : ""}
            {need.timeline ? ` · ${need.timeline}` : ""}
            {need.startupStage ? ` · ${need.startupStage}` : ""}
          </div>
          <p style={{ fontSize: 15, color: "var(--text-2)", lineHeight: 1.65, margin: "0 0 8px", whiteSpace: "pre-wrap" }}>
            {need.description || "No description provided."}
          </p>
          {!expanded && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              {need.tags?.slice(0, 4).map(t => <span key={t} className="tag">{t}</span>)}
              {need.tags?.length > 4 && (
                <span style={{ fontSize: 13, color: "var(--text-3)" }}>+{need.tags.length - 4}</span>
              )}
              {hasRequirements && (
                <span className="admin-req-hint">Requirements included</span>
              )}
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 8, flexShrink: 0, flexWrap: "wrap", justifyContent: "flex-end" }}>
          <button type="button" onClick={onEdit} className="admin-edit-btn">
            Edit
          </button>
          <button type="button" onClick={onDelete} className="admin-delete-btn">
            Delete
          </button>
          <button type="button" onClick={onToggle} style={{
            padding: "8px 14px", borderRadius: "var(--radius)", fontSize: 14, cursor: "pointer",
            background: expanded ? "rgba(117,250,170,0.12)" : "var(--surface-2)",
            border: expanded ? "1px solid rgba(117,250,170,0.3)" : "1px solid var(--border)",
            color: expanded ? "var(--teal)" : "var(--text-2)",
            minHeight: 36, fontWeight: 600
          }}>{expanded ? "Collapse" : "Details"}</button>
        </div>
      </div>

      {expanded && (
        <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid var(--border)" }}>
          <DetailBlock label="Requirements">
            {need.additionalContext || "No additional requirements provided."}
          </DetailBlock>
          {need.tags?.length > 0 && (
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 12 }}>
              {need.tags.map(t => <span key={t} className="tag">{t}</span>)}
            </div>
          )}
          <ActionButtons type="need" feedback={feedback} onFeedback={onFeedback}
            onApprove={onApprove} onReject={onReject} onRequestChanges={onRequestChanges} />
        </div>
      )}
    </div>
  );
}

function EditChallengeModal({ need, onClose, onSave }) {
  const [form, setForm] = useState({
    title: need.title || "",
    description: need.description || "",
    industry: need.industry || "",
    startupStage: need.startupStage || "Any stage",
    status: need.status || "pending_review",
    additionalContext: need.additionalContext || "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  function set(field) {
    return e => setForm(f => ({ ...f, [field]: e.target.value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.title.trim() || !form.description.trim()) {
      setError("Title and description are required.");
      return;
    }
    setError("");
    setSaving(true);
    try {
      await onSave(need.id, form);
    } catch (err) {
      console.error("saveChallengeEdits", err);
      const msg = err?.code === "permission-denied"
        ? "You don’t have permission to update this challenge."
        : (err?.message || "Failed to save changes. Please try again.");
      setError(msg);
      setSaving(false);
    }
  }

  return (
    <div className="admin-modal-overlay" onClick={onClose} role="presentation">
      <div
        className="admin-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-challenge-title"
        onClick={e => e.stopPropagation()}
      >
        <div className="admin-modal-header">
          <h2 id="edit-challenge-title">Edit Challenge</h2>
          <button type="button" className="admin-modal-close" onClick={onClose} aria-label="Close">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="admin-modal-body">
          <div className="form-group">
            <label className="form-label" htmlFor="edit-title">Title</label>
            <input id="edit-title" className="input" value={form.title} onChange={set("title")} required />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="edit-description">Description</label>
            <textarea id="edit-description" className="textarea" style={{ minHeight: 120 }} value={form.description} onChange={set("description")} required />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="edit-category">Category</label>
            <select id="edit-category" className="select" value={form.industry} onChange={set("industry")} required>
              <option value="">Select category</option>
              {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
              {form.industry && !INDUSTRIES.includes(form.industry) && (
                <option value={form.industry}>{form.industry}</option>
              )}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="edit-audience">Target Audience</label>
            <select id="edit-audience" className="select" value={form.startupStage} onChange={set("startupStage")}>
              {STAGES.map(s => <option key={s} value={s}>{s}</option>)}
              {form.startupStage && !STAGES.includes(form.startupStage) && (
                <option value={form.startupStage}>{form.startupStage}</option>
              )}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="edit-status">Status</label>
            <select id="edit-status" className="select" value={form.status} onChange={set("status")}>
              {NEED_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              {form.status && !NEED_STATUSES.some(s => s.value === form.status) && (
                <option value={form.status}>{form.status}</option>
              )}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="edit-requirements">Requirements</label>
            <textarea
              id="edit-requirements"
              className="textarea"
              style={{ minHeight: 100 }}
              value={form.additionalContext}
              onChange={set("additionalContext")}
              placeholder="Constraints, tech stack, compliance, or other requirements..."
            />
          </div>

          {error && (
            <p style={{ fontSize: 15, color: "var(--coral-text)", margin: 0 }} role="alert">{error}</p>
          )}

          <div className="admin-modal-actions">
            <button type="button" className="btn btn-outline" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function collectPitchLinks(pitch) {
  const links = [];
  const push = (label, url) => {
    if (!url || typeof url !== "string") return;
    const trimmed = url.trim();
    if (!trimmed) return;
    links.push({ label, url: trimmed });
  };

  if (Array.isArray(pitch.links)) {
    pitch.links.forEach((item, i) => {
      if (typeof item === "string") push(`Link ${i + 1}`, item);
      else if (item?.url) push(item.label || item.name || `Link ${i + 1}`, item.url);
    });
  }
  push("Website", pitch.website || pitch.url || pitch.link);
  push("Deck", pitch.deckUrl || pitch.deck);
  push("Demo", pitch.demoUrl || pitch.demo);
  push("GitHub", pitch.githubUrl || pitch.github);

  if (Array.isArray(pitch.files)) {
    pitch.files.forEach((item, i) => {
      if (typeof item === "string") push(item.split("/").pop() || `File ${i + 1}`, item);
      else if (item?.url) push(item.name || item.label || `File ${i + 1}`, item.url);
    });
  }
  if (Array.isArray(pitch.attachments)) {
    pitch.attachments.forEach((item, i) => {
      if (typeof item === "string") push(item.split("/").pop() || `Attachment ${i + 1}`, item);
      else if (item?.url) push(item.name || item.label || `Attachment ${i + 1}`, item.url);
    });
  }

  const seen = new Set();
  return links.filter(l => {
    if (seen.has(l.url)) return false;
    seen.add(l.url);
    return true;
  });
}

function PitchCard({ pitch, expanded, onToggle, onApprove, onReject, onRequestChanges, onSendFeedback }) {
  const timeAgo = pitch.createdAt?.toDate ? formatDistanceToNow(pitch.createdAt.toDate(), { addSuffix: true }) : "";
  const hasSavedFeedback = Boolean(pitch.adminFeedback?.trim());
  const [feedbackOpen, setFeedbackOpen] = useState(hasSavedFeedback);
  const [feedbackText, setFeedbackText] = useState(pitch.adminFeedback || "");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const links = collectPitchLinks(pitch);
  const isPending = pitch.status === "pending_review" || pitch.status === "pending";

  useEffect(() => {
    setFeedbackText(pitch.adminFeedback || "");
    if (pitch.adminFeedback?.trim()) setFeedbackOpen(true);
  }, [pitch.id, pitch.adminFeedback]);

  async function handleSendFeedback() {
    if (!feedbackText.trim()) {
      setError("Add feedback before sending.");
      return;
    }
    setError("");
    setSending(true);
    try {
      await onSendFeedback(pitch.id, feedbackText);
    } catch {
      setError("Failed to send feedback. Please try again.");
    } finally {
      setSending(false);
    }
  }

  const pitchSections = [
    ["Solution summary", pitch.summary],
    ["Approach", pitch.approach],
    ["Proposed timeline", pitch.timeline],
    ["Proposed budget", pitch.budget],
    ["Why this startup", pitch.whyUs],
  ].filter(([, v]) => v);

  return (
    <div className="card" style={{
      padding: "16px 18px",
      borderLeft: isPending ? "3px solid #75faaa" : "1px solid var(--border)"
    }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
            <StatusBadge status={pitch.status} />
            {hasSavedFeedback && (
              <span className="admin-feedback-badge">Feedback Provided</span>
            )}
            <span style={{ fontSize: 14, color: "var(--text-3)" }}>{timeAgo}</span>
          </div>
          <div style={{ fontSize: 16, fontWeight: 600, color: "var(--text)", marginBottom: 2 }}>{pitch.startupName}</div>
          <div style={{ fontSize: 14, color: "var(--text-3)" }}>
            Pitching on: {pitch.needTitle || "Untitled challenge"}
            {pitch.corpName ? ` · ${pitch.corpName}` : ""}
          </div>
        </div>
        <button type="button" onClick={onToggle} style={{
          padding: "8px 14px", borderRadius: "var(--radius)", fontSize: 14, cursor: "pointer",
          background: expanded ? "rgba(117,250,170,0.12)" : "var(--surface-2)",
          border: expanded ? "1px solid rgba(117,250,170,0.3)" : "1px solid var(--border)",
          color: expanded ? "var(--teal)" : "var(--text-2)",
          minHeight: 36, fontWeight: 600, flexShrink: 0
        }}>{expanded ? "Collapse" : "Review"}</button>
      </div>

      {pitchSections.map(([label, val]) => (
        <DetailBlock key={label} label={label}>{val}</DetailBlock>
      ))}

      <div style={{ marginBottom: 14 }}>
        <div style={{
          fontSize: 13, fontWeight: 600, color: "var(--text-3)",
          textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8
        }}>Team</div>
        <div className="admin-team-grid">
          <div className="admin-team-item">
            <span>Startup</span>
            <strong>{pitch.startupName || "—"}</strong>
          </div>
          <div className="admin-team-item">
            <span>Contact</span>
            <strong>{pitch.startupContact || "—"}</strong>
          </div>
          <div className="admin-team-item">
            <span>Email</span>
            {pitch.startupEmail ? (
              <a href={`mailto:${pitch.startupEmail}`}>{pitch.startupEmail}</a>
            ) : (
              <strong>—</strong>
            )}
          </div>
        </div>
      </div>

      <div style={{ marginBottom: 14 }}>
        <div style={{
          fontSize: 13, fontWeight: 600, color: "var(--text-3)",
          textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8
        }}>Links & files</div>
        {links.length === 0 ? (
          <p style={{ fontSize: 15, color: "var(--text-3)", margin: 0 }}>No links or files attached.</p>
        ) : (
          <div className="admin-link-list">
            {links.map(link => (
              <a key={link.url} href={link.url} target="_blank" rel="noopener noreferrer" className="admin-link-chip">
                {link.label}
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3"/>
                </svg>
              </a>
            ))}
          </div>
        )}
      </div>

      <div className="admin-feedback-panel">
        <button
          type="button"
          className="admin-feedback-toggle"
          aria-expanded={feedbackOpen}
          onClick={() => setFeedbackOpen(o => !o)}
        >
          <span>Admin Feedback</span>
          <svg
            width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
            style={{ transform: feedbackOpen ? "rotate(180deg)" : "none", transition: "transform 0.18s ease" }}
            aria-hidden="true"
          >
            <path d="M6 9l6 6 6-6"/>
          </svg>
        </button>

        {feedbackOpen && (
          <div className="admin-feedback-body">
            <label className="form-label" htmlFor={`feedback-${pitch.id}`}>Message to startup</label>
            <textarea
              id={`feedback-${pitch.id}`}
              className="textarea"
              style={{ minHeight: 90 }}
              value={feedbackText}
              onChange={e => setFeedbackText(e.target.value)}
              placeholder="Share guidance, approval notes, or requested changes with the startup…"
            />
            {error && <p style={{ fontSize: 15, color: "var(--coral-text)", margin: 0 }} role="alert">{error}</p>}
            <button
              type="button"
              className="btn btn-primary"
              style={{ alignSelf: "flex-start", minHeight: 40 }}
              onClick={handleSendFeedback}
              disabled={sending}
            >
              {sending ? "Sending…" : "Send Feedback to Startup"}
            </button>
          </div>
        )}
      </div>

      {expanded && (
        <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid var(--border)" }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button type="button" onClick={onApprove} style={{
              flex: 1, minWidth: 120, padding: "9px", borderRadius: "var(--radius)", fontSize: 15, fontWeight: 600, cursor: "pointer",
              background: "var(--teal-light)", color: "var(--teal)", border: "1px solid rgba(117,250,170,0.25)"
            }}>✓ Approve</button>
            <button type="button" onClick={onRequestChanges} style={{
              flex: 1, minWidth: 120, padding: "9px", borderRadius: "var(--radius)", fontSize: 15, fontWeight: 600, cursor: "pointer",
              background: "var(--purple-light)", color: "var(--purple-text)", border: "1px solid rgba(83,74,183,0.25)"
            }}>↩ Request changes</button>
            <button type="button" onClick={onReject} style={{
              flex: 1, minWidth: 120, padding: "9px", borderRadius: "var(--radius)", fontSize: 15, fontWeight: 600, cursor: "pointer",
              background: "var(--coral-light)", color: "var(--coral-text)", border: "1px solid rgba(220,80,60,0.2)"
            }}>✗ Reject</button>
          </div>
        </div>
      )}
    </div>
  );
}
